import { CollapsibleFilters } from "../components/common/CollapsibleFilters";
import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Stack,
  Typography,
  Input,
  Select,
  Option,
  Checkbox,
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
} from '@mui/joy';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import type { View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import moment from 'moment';
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { AddJobModal } from '../components/jobs';
import { TaskCreateModal } from '../components/tasks';
import { DetailDrawer } from '../components/common/DetailDrawer';
import { MobileDispatchAgenda } from '../components/scheduling/MobileDispatchAgenda';
import { ProductionPlanner } from '../components/scheduling/ProductionPlanner';
import { toEvent, overlaps, localDay } from '../components/scheduling/calendar';
import type { DispatchEvent, EventKind } from '../components/scheduling/calendar';
import { formatForDatetimeLocalInput, setOrganizationTimezone, parseFromDatetimeLocalInput } from '../lib/timezoneUtils';

const DragCalendar = withDragAndDrop<DispatchEvent>(Calendar);
const localizer = momentLocalizer(moment);
const kinds: EventKind[] = ['job', 'task', 'work_order', 'maintenance', 'meeting'];
const labels: Record<EventKind, string> = {
  job: 'Jobs',
  task: 'Tasks',
  work_order: 'Work Orders',
  maintenance: 'Maintenance',
  meeting: 'Meetings',
};
const colors: Record<EventKind, string> = {
  job: '#1769aa',
  task: '#247a4b',
  work_order: '#a95b12',
  maintenance: '#7a42a8',
  meeting: '#216b78',
};
const dateText = (date: Date) => moment(date).format('YYYY-MM-DD');
const localInput = (date: Date) => moment(date).format('YYYY-MM-DDTHH:mm');
const load = async (url: string) => (await apiClient.getAllPages<any>(url)).data;

export const Scheduling: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const production = params.get('workspace') === 'production';
  const { user } = useAuth();
  const timezone = useQuery({
    queryKey: ['organization-timezone', user?.organization, user?.id],
    queryFn: async () => {
      const { data } = await apiClient.get<{ time_zone: string }>('/api/scheduling/context/');
      if (!data.time_zone) throw new Error('Company time zone is not set.');
      // Validate the saved zone before showing or editing any timed work.
      new Intl.DateTimeFormat('en', { timeZone: data.time_zone }).format();
      setOrganizationTimezone(data.time_zone);
      return data.time_zone;
    },
    refetchOnWindowFocus: true,
  });
  if (timezone.isError) return <Alert color="warning">Unable to load the company time zone. Please retry before scheduling work.<Button onClick={() => timezone.refetch()}>Retry</Button></Alert>;
  if (!timezone.data) return <Typography role="status">Loading company time zone…</Typography>;
  return <Box><Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mb: 2 }}>
    <Button variant={production ? 'outlined' : 'solid'} onClick={() => { const next = new URLSearchParams(params); next.delete('workspace'); next.delete('page'); setParams(next); }}>Dispatch Calendar</Button>
    <Button variant={production ? 'solid' : 'outlined'} onClick={() => { const next = new URLSearchParams(params); next.set('workspace', 'production'); setParams(next); }}>Production Readiness</Button>
  </Stack>{production ? <ProductionPlanner /> : <DispatchScheduling zone={timezone.data} />}</Box>;
};

const DispatchScheduling: React.FC<{ zone: string }> = ({ zone }) => {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const scope = [user?.organization, user?.id];
  const currentDate =
    params.get('date') && moment(params.get('date'), 'YYYY-MM-DD', true).isValid()
      ? localDay(params.get('date')!)
      : new Date(formatForDatetimeLocalInput(new Date(), zone));
  const view = (
    ['month', 'week', 'day', 'agenda'].includes(params.get('view') || '')
      ? params.get('view')
      : window.innerWidth < 960
        ? 'agenda'
        : 'month'
  ) as View;
  const capacity = Number(params.get('capacity') || 0);
  const completed = params.get('completed') === 'true';
  const shown = (params.get('types') ?? kinds.join(',')).split(',');
  const assignee = params.get('assignee') || '';
  const job = params.get('job') || '';
  const status = params.get('status') || '';
  const update = (values: Record<string, string>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([k, v]) => next.set(k, v));
    setParams(next, { replace: true });
  };
  const urls = [
    `/api/jobs/scheduled/?include_completed=${completed}`,
    `/api/tasks/scheduled/?include_completed=${completed}`,
    `/api/subcontractor-work-orders/scheduled/?include_completed=${completed}`,
    `/api/maintenance/appointments/scheduled/?include_completed=${completed}`,
    `/api/scheduling/meetings/?status=${completed ? '' : 'scheduled'}`,
  ];
  const feeds = useQueries({
    queries: urls.map((url, i) => ({
      queryKey: ['dispatch', ...scope, kinds[i], completed],
      queryFn: () => load(url),
    })),
  });
  const events = feeds.flatMap((feed, i) =>
    (feed.data || []).map((r) => toEvent(kinds[i], r, zone)).filter(Boolean),
  ) as DispatchEvent[];
  const visible = events.filter(
    (e) =>
      e.status !== 'cancelled' &&
      shown.includes(e.kind) &&
      (!assignee || e.assignee === assignee) &&
      (!job || e.job === job) &&
      (!status || e.status === status),
  );
  const people = Array.from(new Map(events.map((e) => [e.assignee, e.assigneeName])).entries());
  const statuses = Array.from(new Set(events.map((e) => e.status).filter(Boolean)));
  const [selected, setSelected] = useState<DispatchEvent | null>(null);
  const [pending, setPending] = useState<{ event: DispatchEvent; start: Date; end: Date } | null>(
    null,
  );
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [ack, setAck] = useState(false);
  const [create, setCreate] = useState<'job' | 'task' | null>(null);
  const [createDate, setCreateDate] = useState(dateText(currentDate));
  const [chooser, setChooser] = useState(false);
  const [backlog, setBacklog] = useState(false);
  const [backlogPage, setBacklogPage] = useState(1);
  const backlogQueries = useQueries({
    queries: ['job', 'task'].map((kind) => ({
      queryKey: ['dispatch-backlog', ...scope, kind],
      enabled: backlog,
      queryFn: () =>
        load(kind === 'job' ? '/api/jobs/?unscheduled=true' : '/api/tasks/?unscheduled=true'),
    })),
  });
  const unscheduled = backlogQueries.flatMap((q, i) =>
    (q.data || []).map((r) => ({ kind: i === 0 ? 'job' : 'task', record: r })),
  ) as Array<{ kind: 'job' | 'task'; record: any }>;
  const connection = useQuery({
    queryKey: ['dispatch-connections', ...scope],
    queryFn: () => load('/api/scheduling/calendars/'),
  });
  const savedKey = `dispatch-views:${scope.join(':')}`;
  const [saved, setSaved] = useState<Record<string, string>>(() => {
    try {
      return JSON.parse(localStorage.getItem(savedKey) || '{}');
    } catch {
      return {};
    }
  });
  const [viewName, setViewName] = useState('');
  const beginMove = (event: DispatchEvent, newStart = event.start, newEnd = event.end) => {
    mutation.reset();
    setPending({ event, start: newStart, end: newEnd });
    setStart(event.allDay ? dateText(newStart) : localInput(newStart));
    const inclusive = new Date(newEnd);
    if (event.allDay) inclusive.setDate(inclusive.getDate() - 1);
    setEnd(event.allDay ? dateText(inclusive) : localInput(newEnd));
    setAck(false);
  };
  const details = useQuery({
    queryKey: ['dispatch-details', pending?.event.id, selected?.id],
    enabled: !!(pending || selected) && (pending?.event || selected)?.kind !== 'meeting',
    queryFn: async () => {
      const e = pending?.event || selected!;
      return (
        await apiClient.get<any>(`/api/scheduling/reschedule/?type=${e.kind}&id=${e.record.id}`)
      ).data;
    },
  });
  const mutation = useMutation({
    mutationFn: async () => {
      if (!pending || !details.data) throw Error('Reload the event first');
      const type = pending.event.kind;
      if (type === 'maintenance' && start.slice(0, 10) !== end.slice(0, 10))
        throw Error('Maintenance appointments must start and end on the same day');
      const changes =
        type === 'job'
          ? { build_date: start }
          : type === 'task'
            ? { due_date: parseFromDatetimeLocalInput(start, zone).toISOString() }
            : type === 'work_order'
              ? { start_date: start, end_date: end }
              : {
                  scheduled_date: start.slice(0, 10),
                  scheduled_time_start: start.slice(11),
                  scheduled_time_end: end.slice(11),
                };
      return apiClient.post('/api/scheduling/reschedule/', {
        type,
        id: pending.event.record.id,
        changes,
        expected: details.data.current,
        acknowledge_conflicts: ack,
      });
    },
    onSuccess: () => {
      setPending(null);
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['dispatch'] });
      qc.invalidateQueries({ queryKey: ['dispatch-backlog'] });
      qc.invalidateQueries({ queryKey: ['dispatch-details'] });
    },
  });
  const dayEvents = visible.filter(
    (e) =>
      e.start <
        new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1) &&
      e.end > localDay(dateText(currentDate)),
  );
  const counts = people
    .map(([id, name]) => ({
      id,
      name,
      count: dayEvents.filter((e) => e.assignee === id).length,
      conflicts: dayEvents.filter(
        (e) => e.assignee === id && dayEvents.some((other) => overlaps(e, other)),
      ).length,
    }))
    .filter((p) => p.count);
  const openRecord = (event: DispatchEvent) => {
    sessionStorage.setItem('dispatch-return', `/operations/scheduling?${params}`);
    navigate(event.href);
  };
  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Typography level="h3" sx={{ mr: 'auto' }}>
          Scheduling
        </Typography>
        <Button component={Link} to="/scheduling/meetings" variant="plain">
          My Meetings
        </Button>
        <Button component={Link} to="/scheduling/teams" variant="plain">
          Teams
        </Button>
        <Button component={Link} to="/settings/scheduling" variant="plain">
          Availability
        </Button>
      </Stack>
      <Typography level="body-sm" sx={{ mb: 2 }}>
        Times shown in {zone}. Set in Settings → Company Details → Time Zone.{' '}
        {connection.isError
          ? 'Calendar connection status unavailable.'
          : connection.data?.length
            ? `${connection.data.filter((c) => c.is_active).length} active calendar connection(s)`
            : 'No external calendar connected.'}
      </Typography>
      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', mb: 1 }}>
        {connection.data?.map((c) => (
          <Chip key={c.id} color={c.is_expired ? 'warning' : 'neutral'}>
            {c.provider_display || c.provider}:{' '}
            {c.is_expired ? 'needs refresh' : c.is_active ? 'connected' : 'inactive'}
          </Chip>
        ))}
      </Stack>
      <Card sx={{ mb: 2 }}>
        <CollapsibleFilters label="Search, Filters & Sort"><Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          {kinds.map((kind) => (
            <Chip
              key={kind}
              variant={shown.includes(kind) ? 'soft' : 'outlined'}
              onClick={() =>
                update({
                  types: (shown.includes(kind)
                    ? shown.filter((k) => k !== kind)
                    : [...shown, kind]
                  ).join(','),
                })
              }
            >
              {labels[kind]} · {events.filter((e) => e.kind === kind).length}
            </Chip>
          ))}
          <Checkbox
            label="Include completed"
            checked={completed}
            onChange={(e) => update({ completed: String(e.target.checked) })}
          />
        </Stack>
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Select
            aria-label="Assignee or crew"
            value={assignee}
            onChange={(_, v) => update({ assignee: v || '' })}
          >
            <Option value="">All people / crews</Option>
            {people.map(([id, name]) => (
              <Option key={id} value={id}>
                {name}
              </Option>
            ))}
          </Select>
          <Input
            aria-label="Daily assignment target"
            type="number"
            placeholder="Daily assignment target"
            value={capacity || ''}
            onChange={(e) => update({ capacity: e.target.value })}
            slotProps={{ input: { min: 1 } }}
          />
          <Input
            aria-label="Job ID filter"
            placeholder="Filter by job ID"
            value={job}
            onChange={(e) => update({ job: e.target.value })}
          />
          <Select
            aria-label="Event status"
            value={status}
            onChange={(_, v) => update({ status: v || '' })}
          >
            <Option value="">All statuses</Option>
            {statuses.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Select>
          <Select
            aria-label="Saved calendar view"
            placeholder="Saved views"
            onChange={(_, v) => v && setParams(new URLSearchParams(saved[String(v)]))}
          >
            {Object.keys(saved).map((n) => (
              <Option key={n} value={n}>
                {n}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Name this view"
            aria-label="Calendar view name"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
          />
          <Button
            disabled={!viewName.trim()}
            variant="outlined"
            onClick={() => {
              const next = { ...saved, [viewName.trim()]: params.toString() };
              localStorage.setItem(savedKey, JSON.stringify(next));
              setSaved(next);
              setViewName('');
            }}
          >
            Save View
          </Button>
        </Stack>
</CollapsibleFilters>
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={() => {
              setCreateDate(dateText(currentDate));
              setChooser(true);
            }}
          >
            Schedule New Work
          </Button>
          <Button variant="outlined" onClick={() => setBacklog(!backlog)}>
            Unscheduled Work
          </Button>
          <Button variant="plain" onClick={() => qc.invalidateQueries({ queryKey: ['dispatch'] })}>
            Refresh
          </Button>
        </Stack>
      </Card>
      {feeds.some((f) => f.isError) && (
        <Alert color="warning" sx={{ mb: 2 }}>
          Some events could not be loaded:{' '}
          {feeds
            .map((f, i) => (f.isError ? labels[kinds[i]] : null))
            .filter(Boolean)
            .join(', ')}
          . <Button onClick={() => feeds.forEach((f) => f.isError && f.refetch())}>Retry</Button>
        </Alert>
      )}
      {counts.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <Typography level="title-sm">Daily workload · {dateText(currentDate)}</Typography>
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            {counts.map((p) => (
              <Chip
                key={p.id}
                color={p.conflicts || (capacity > 0 && p.count > capacity) ? 'warning' : 'neutral'}
              >
                {p.name}: {p.count}
                {capacity ? ` / ${capacity}` : ''} assignment(s)
                {p.conflicts ? ` · ${p.conflicts} overlap` : ''}
              </Chip>
            ))}
          </Stack>
          <Typography level="body-xs">
            Assignment counts help review capacity; all-day work occupies the full day.
          </Typography>
        </Card>
      )}
      {backlog && (
        <Card sx={{ mb: 2 }}>
          <Typography level="title-lg">
            Unscheduled jobs and tasks · {unscheduled.length}
          </Typography>
          {backlogQueries.some((q) => q.isLoading) && (
            <Typography>Loading unscheduled work…</Typography>
          )}
          {backlogQueries.some((q) => q.isError) && (
            <Button onClick={() => backlogQueries.forEach((q) => q.refetch())}>
              Retry Backlog
            </Button>
          )}
          {unscheduled.slice((backlogPage - 1) * 10, backlogPage * 10).map(({ kind, record }) => (
            <Stack
              key={`${kind}-${record.id}`}
              direction="row"
              sx={{ gap: 1, alignItems: 'center' }}
            >
              <Typography sx={{ flex: 1 }}>
                {record.name || record.title || `${record.job_number} · ${record.address_1}`}
              </Typography>
              <Button
                size="sm"
                onClick={() => {
                  const event = toEvent(kind, {
                    ...record,
                    build_date: dateText(currentDate),
                    due_date: parseFromDatetimeLocalInput(
                      `${dateText(currentDate)}T09:00`,
                      zone,
                    ).toISOString(),
                  }, zone)!;
                  beginMove(event);
                }}
              >
                Schedule
              </Button>
            </Stack>
          ))}
          <Stack direction="row">
            <Button disabled={backlogPage === 1} onClick={() => setBacklogPage((p) => p - 1)}>
              Previous
            </Button>
            <Typography>Page {backlogPage}</Typography>
            <Button
              disabled={backlogPage * 10 >= unscheduled.length}
              onClick={() => setBacklogPage((p) => p + 1)}
            >
              Next
            </Button>
          </Stack>
        </Card>
      )}
      <Card>
        {view === 'agenda' && (
          <MobileDispatchAgenda
            events={visible}
            date={currentDate}
            onSelect={setSelected}
            onNavigate={(date) => update({ date: dateText(date) })}
          />
        )}
        <Box
          sx={{
            display: view === 'agenda' ? { xs: 'none', md: 'block' } : 'block',
            height: { xs: 650, md: 760 },
            '& .rbc-toolbar': { gap: 1, flexWrap: 'wrap' },
            '& .rbc-agenda-view table': { minWidth: 0, tableLayout: 'fixed' },
            '& .rbc-agenda-event-cell': { overflowWrap: 'anywhere' },
            '& .rbc-toolbar button': { color: 'text.primary', borderColor: 'divider' },
            '& .rbc-toolbar button.rbc-active': {
              bgcolor: 'primary.softBg',
              color: 'primary.softColor',
            },
            '& .rbc-today': { bgcolor: 'primary.softBg' },
            '& .rbc-off-range-bg': { bgcolor: 'background.level1' },
            '& .rbc-month-view, & .rbc-time-view, & .rbc-header, & .rbc-month-row, & .rbc-day-bg': {
              borderColor: 'divider',
            },
          }}
        >
          <DragCalendar
            localizer={localizer}
            getNow={() => new Date(formatForDatetimeLocalInput(new Date(), zone))}
            events={visible}
            startAccessor="start"
            endAccessor="end"
            allDayAccessor="allDay"
            view={view}
            onView={(v) => update({ view: v })}
            date={currentDate}
            onNavigate={(date) => update({ date: dateText(date) })}
            selectable
            onSelectSlot={({ start }) => {
              setCreateDate(dateText(new Date(start)));
              setChooser(true);
            }}
            onSelectEvent={setSelected}
            onEventDrop={({ event, start, end }) =>
              beginMove(event, new Date(start), new Date(end))
            }
            draggableAccessor={(event) =>
              event.kind !== 'meeting' &&
              (event.kind !== 'job' || !!user?.is_organization_admin || !!user?.can_schedule_builds)
            }
            resizable={false}
            eventPropGetter={(event) => ({
              style: { backgroundColor: colors[event.kind], borderColor: colors[event.kind] },
            })}
            components={{
              event: ({ event }) => (
                <span>
                  {event.title} · {event.assigneeName}
                </span>
              ),
            }}
          />
        </Box>
      </Card>
      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.title || 'Event'}
      >
        {selected && (
          <>
            <Typography>
              {selected.assigneeName} · {selected.status}
            </Typography>
            {selected.kind === 'meeting' && selected.record.calendar_sync_state && (
              <Alert color={selected.record.calendar_sync_error ? 'warning' : 'neutral'}>
                External calendar: {selected.record.calendar_sync_state.replaceAll('_', ' ')}.
                {selected.record.calendar_sync_error}
              </Alert>
            )}
            <Typography>
              {selected.allDay ? dateText(selected.start) : localInput(selected.start)}
            </Typography>
            {selected.address && (
              <Button
                component="a"
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selected.address)}`}
                target="_blank"
                rel="noreferrer"
              >
                Directions To Property
              </Button>
            )}
            <Button onClick={() => openRecord(selected)}>
              Open {labels[selected.kind].replace(/s$/, '')}
            </Button>
            {selected.kind !== 'meeting' && (
              <Button variant="outlined" onClick={() => beginMove(selected)}>
                Reschedule
              </Button>
            )}
            <Typography level="body-sm">
              Use the record's communication actions to notify the customer after reviewing a
              change.
            </Typography>
            <Typography level="title-sm">Schedule history</Typography>
            {details.data?.history.map((entry: any) => (
              <Box key={entry.id}>
                <Typography level="body-sm">
                  {entry.actor_name} · {new Date(entry.created_at).toLocaleString(undefined, { timeZone: zone })}
                </Typography>
                {Object.keys(entry.after).map((field) => (
                  <Typography level="body-xs" key={field}>
                    {field.replaceAll('_', ' ')}: {entry.before[field] || 'Unscheduled'} →{' '}
                    {entry.after[field]}
                  </Typography>
                ))}
              </Box>
            ))}
          </>
        )}
      </DetailDrawer>
      <Modal open={!!pending} onClose={() => !mutation.isPending && setPending(null)}>
        <ModalDialog sx={{ width: 560, maxWidth: '95vw' }}>
          <ModalClose />
          <Typography level="h4">Review schedule change</Typography>
          <Typography>{pending?.event.title}</Typography>
          <Typography level="body-sm">
            Previously:{' '}
            {details.data
              ? Object.values(details.data.current).filter(Boolean).map((value) =>
                  typeof value === 'string' && /T|\d \d{2}:/.test(value)
                    ? new Date(value).toLocaleString(undefined, { timeZone: zone })
                    : String(value),
                ).join(' · ') || 'Unscheduled'
              : 'Loading current schedule…'}
          </Typography>
          <FormControl>
            <FormLabel>New start / due date</FormLabel>
            <Input
              type={pending?.event.allDay ? 'date' : 'datetime-local'}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </FormControl>
          {pending?.event.kind !== 'job' && pending?.event.kind !== 'task' && (
            <FormControl>
              <FormLabel>New end</FormLabel>
              <Input
                type={pending?.event.allDay ? 'date' : 'datetime-local'}
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </FormControl>
          )}
          <Checkbox
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            label="I reviewed the assignee's workload for this day"
          />
          {mutation.isError && (
            <Alert color="danger">
              {(mutation.error as any).responseData?.detail || mutation.error.message}
              <Button variant="plain" onClick={() => details.refetch()}>
                Reload Event
              </Button>
            </Alert>
          )}
          {details.isError && (
            <Button onClick={() => details.refetch()}>Retry Loading Event</Button>
          )}
          <Button
            loading={mutation.isPending}
            disabled={!details.data || !start}
            onClick={() => mutation.mutate()}
          >
            Save Schedule Change
          </Button>
        </ModalDialog>
      </Modal>
      <Modal open={chooser} onClose={() => setChooser(false)}>
        <ModalDialog>
          <Typography level="h4">Schedule work for {createDate}</Typography>
          <Button
            onClick={() => {
              setCreate('job');
              setChooser(false);
            }}
          >
            Create Job
          </Button>
          <Button
            onClick={() => {
              setCreate('task');
              setChooser(false);
            }}
          >
            Create Task
          </Button>
        </ModalDialog>
      </Modal>
      <AddJobModal
        open={create === 'job'}
        onClose={() => setCreate(null)}
        defaultBuildDate={createDate}
        onSuccess={() => {
          setCreate(null);
          qc.invalidateQueries({ queryKey: ['dispatch'] });
        }}
      />
      <TaskCreateModal
        open={create === 'task'}
        onClose={() => setCreate(null)}
        defaultDueDate={createDate}
        timezone={zone}
        onSuccess={(task) => {
          setCreate(null);
          // Use the saved date in the same timezone as the calendar event.
          const event = toEvent('task', task, zone);
          if (event && !Number.isNaN(event.start.getTime())) {
            update({ date: dateText(event.start) });
          }
          qc.invalidateQueries({ queryKey: ['dispatch'] });
        }}
      />
    </Box>
  );
};
